import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import {
  Box,
  Container,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Typography,
  Button,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  Stack,
} from '@mui/material'
import {
  ArrowBack as BackIcon,
  ArrowForward as ForwardIcon,
} from '@mui/icons-material'
import {
  ServicePackageFormData,
  FormStep,
  ValidationErrors,
} from '../../types/servicePackage'
import { useNavigate } from 'react-router-dom'
import { alpha } from '@mui/material/styles'
import { Section1Basics } from './Section1Basics'
import { Section2ClinicalCoding } from './Section2ClinicalCoding'
import { Section3PricingBilling } from './Section3PricingBilling'
import { Section4SchedulingRules } from './Section4SchedulingRules'
import { Section5Documentation } from './Section5Documentation'
import { Section6VisibilityMatching } from './Section6VisibilityMatching'
import {
  validateStep1Basics,
  validateStep2ClinicalCoding,
  validateStep3PricingBilling,
  validateStep4SchedulingRules,
  validateStep5Documentation,
  validateStep6VisibilityMatching,
  validateAllSteps,
} from '../../utils/servicePackageValidation'
import { useAppSelector } from '@/store'

interface ServicePackageStepperProps {
  initialData?: Partial<ServicePackageFormData>
  onSave?: (data: ServicePackageFormData) => Promise<void>
  onPublish?: (data: ServicePackageFormData) => Promise<void>
  mode?: 'create' | 'edit'
  providerId?: string
}

const steps = [
  'Basics',
  'Clinical Coding',
  'Pricing & Billing',
  'Scheduling & Rules',
  'Documentation',
  'Visibility & Matching',
]

const STEP_SECTION_MAP: Record<FormStep, keyof ServicePackageFormData> = {
  1: 'basics',
  2: 'clinicalCoding',
  3: 'pricingBilling',
  4: 'schedulingRules',
  5: 'documentation',
  6: 'visibilityMatching',
}

const getFirstErrorPath = (
  errors: ValidationErrors,
  parentPath = ''
): string | null => {
  const keys = Object.keys(errors || {})

  for (const key of keys) {
    const value = errors[key]
    const currentPath = parentPath ? `${parentPath}.${key}` : key

    if (typeof value === 'string') {
      return currentPath
    }

    if (value && typeof value === 'object') {
      const nestedPath = getFirstErrorPath(
        value as ValidationErrors,
        currentPath
      )
      if (nestedPath) {
        return nestedPath
      }
    }
  }

  return null
}

const getDefaultFormData = (): ServicePackageFormData => ({
  // Section 1: Basics
  basics: {
    serviceTitle: '',
    discipline: '',
    modality: ['in_person'],
    shortDescription: '',
    detailedDescription: '',
    ageRange: {
      min: null,
      max: null,
    },
    languages: [],
    targetGender: [],
  },

  // Section 2: Clinical Coding
  clinicalCoding: {
    cptCodes: [],
    icd10Codes: [],
    medicalNecessityNote: '',
    hcpcsLevel: [],
    modifier: '',
    pos: '',
  },

  // Section 3: Pricing & Billing
  pricingBilling: {
    privatePayRate: null,
    acceptInsurance: false,
    acceptedPayers: [],
    commissionPercentage: 15, // Mocked value
  },

  // Section 4: Scheduling & Rules
  schedulingRules: {
    sessionLengthMin: 60,
    session_buffer: 0,
    travelOnsiteSurcharge: null,
    cancellationWindowHours: null,
    noShowFee: null,
    lateCancellationFee: null,
    inPersonSettings: {
      serviceArea: 'radius_miles',
      radiusMiles: 10,
      zipCodes: [],
    },
  },

  // Section 5: Documentation
  documentation: {
    reportsRequired: [],
    exportFormats: [],
    contractAttachments: [],
  },

  // Section 6: Visibility & Matching
  visibilityMatching: {
    specialties: [],
    bodyRegions: [],
    keywords: [],
    showInSearch: true,
    states: [],
  },

  // Status info
  status: 'draft',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
})




export const ServicePackageStepper: React.FC<ServicePackageStepperProps> = ({
  initialData,
  onSave,
  onPublish,
  mode = 'create',
  providerId,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()

  const { user } = useAppSelector(state => state.auth)
  const selectedProviderId = useAppSelector(state => state.app.selectedAgencyProviderId);

  // console.log("user agency: ", user)
  // console.log("initial data agency: ",)

  const redirectTimeoutRef = useRef<number | null>(null)
  const defaultFormDataRef =
    useRef<ServicePackageFormData>(getDefaultFormData())
  const isEditMode = mode === 'edit'
  const [originalStatus, setOriginalStatus] = useState<
    ServicePackageFormData['status']
  >((initialData?.status as ServicePackageFormData['status']) ?? 'draft')

  const mergeWithDefaults = useCallback(
    (data?: Partial<ServicePackageFormData>): ServicePackageFormData => {
      const base = defaultFormDataRef.current
      if (!data) {
        return { ...base }
      }

      return {
        ...base,
        ...data,
        basics: {
          ...base.basics,
          ...(data.basics ?? {}),
        },
        clinicalCoding: {
          ...base.clinicalCoding,
          ...(data.clinicalCoding ?? {}),
          cptCodes: data.clinicalCoding?.cptCodes
            ? [...data.clinicalCoding.cptCodes]
            : [...base.clinicalCoding.cptCodes],
          icd10Codes: data.clinicalCoding?.icd10Codes
            ? [...data.clinicalCoding.icd10Codes]
            : [...base.clinicalCoding.icd10Codes],
          hcpcsLevel: data.clinicalCoding?.hcpcsLevel
            ? [...data.clinicalCoding.hcpcsLevel]
            : [...base.clinicalCoding.hcpcsLevel],
          modifier:
            data.clinicalCoding?.modifier ?? base.clinicalCoding.modifier,
          pos: data.clinicalCoding?.pos ?? base.clinicalCoding.pos,
        },
        pricingBilling: {
          ...base.pricingBilling,
          ...(data.pricingBilling ?? {}),
        },
        schedulingRules: {
          ...base.schedulingRules,
          ...(data.schedulingRules ?? {}),
          inPersonSettings: {
            ...base.schedulingRules.inPersonSettings,
            ...(data.schedulingRules?.inPersonSettings ?? {}),
          },
        },
        documentation: {
          ...base.documentation,
          ...(data.documentation ?? {}),
          contractAttachments: data.documentation?.contractAttachments
            ? [...data.documentation.contractAttachments]
            : [...base.documentation.contractAttachments],
        },
        visibilityMatching: {
          ...base.visibilityMatching,
          ...(data.visibilityMatching ?? {}),
        },
        status: data.status ?? base.status,
        createdAt: data.createdAt ?? base.createdAt,
        updatedAt: data.updatedAt ?? base.updatedAt,
        id: data.id ?? base.id,
      }
    },
    []
  )

  const [activeStep, setActiveStep] = useState<FormStep>(1) // Start from step 1 (Basics)
  const [formData, setFormData] = useState<ServicePackageFormData>(() =>
    mergeWithDefaults(initialData)
  )
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'info' | 'warning'
  }>({
    open: false,
    message: '',
    severity: 'info',
  })

  const showSnackbar = useCallback(
    (
      message: string,
      severity: 'success' | 'error' | 'info' | 'warning' = 'info'
    ) => {
      setSnackbar({ open: true, message, severity })
    },
    []
  )

  const getErrorMessage = useCallback((error: unknown, fallback: string) => {
    if (
      typeof error === 'object' &&
      error !== null &&
      'response' in error &&
      (error as { response?: { data?: { message?: string } } }).response?.data
        ?.message
    ) {
      return (
        (error as { response?: { data?: { message?: string } } }).response?.data
          ?.message || fallback
      )
    }

    if (error instanceof Error && error.message) {
      return error.message
    }

    return fallback
  }, [])

  const scheduleRedirectToList = useCallback(() => {
    if (redirectTimeoutRef.current) {
      window.clearTimeout(redirectTimeoutRef.current)
    }

    redirectTimeoutRef.current = window.setTimeout(() => {
      navigate(`/agency-provider-service-packages/${selectedProviderId}`)
      redirectTimeoutRef.current = null
    }, 2000)
  }, [navigate, selectedProviderId])

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        window.clearTimeout(redirectTimeoutRef.current)
        redirectTimeoutRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    setFormData(mergeWithDefaults(initialData))
    setValidationErrors({})
    setActiveStep(1)
    setOriginalStatus(
      (initialData?.status as ServicePackageFormData['status']) ?? 'draft'
    )
  }, [initialData, mergeWithDefaults])

  const stepFieldSelectorMap = useMemo<
    Record<FormStep, Record<string, string>>
  >(
    () => ({
      1: {
        serviceTitle: '#service-title-field',
        discipline: '#discipline-field',
        modality: '#modality-field',
        shortDescription: '#short-description-field',
        detailedDescription: '#detailed-description-field',
        'ageRange.min': '#age-min-field',
        'ageRange.max': '#age-max-field',
        ageRange: '#age-range-field',
        languages: '#languages-field',
        targetGender: '#target-gender-field',
        '*': '#service-package-stepper-root',
      },
      2: {
        cptCodes: '#cpt-codes-section',
        'cptCodes.0.code': '#cpt-codes-section',
        'cptCodes.0.description': '#cpt-codes-section',
        'cptCodes.0.quantity': '#cpt-codes-section',
        icd10Codes: '#icd-codes-section',
        'icd10Codes.0.code': '#icd-codes-section',
        medicalNecessityNote: '#medical-necessity-field',
        '*': '#service-package-stepper-root',
      },
      3: {
        privatePayRate: '#private-pay-rate-field',
        acceptedPayers: '#accepted-payers-section',
        '*': '#service-package-stepper-root',
      },
      4: {
        sessionLengthMin: '#session-length-field',
        'inPersonSettings.radiusKm': '#service-radius-field',
        'inPersonSettings.zipCodes': '#service-zipcodes-field',
        '*': '#scheduling-rules-root',
      },
      5: {
        '*': '#service-package-stepper-root',
      },
      6: {
        '*': '#service-package-stepper-root',
      },
    }),
    []
  )

  const scrollToFirstErrorForStep = useCallback(
    (step: FormStep, errors: ValidationErrors) => {
      if (typeof window === 'undefined') return

      const errorPath = getFirstErrorPath(errors)
      if (!errorPath) return

      const fieldMap = stepFieldSelectorMap[step] || {}
      let lookupPath = errorPath
      let selector: string | undefined

      while (lookupPath && !selector) {
        selector = fieldMap[lookupPath]
        if (!selector) {
          const lastDot = lookupPath.lastIndexOf('.')
          if (lastDot === -1) break
          lookupPath = lookupPath.substring(0, lastDot)
        }
      }

      if (!selector) {
        selector = fieldMap['*'] || '#service-package-stepper-root'
      }

      const element =
        selector && (document.querySelector(selector) as HTMLElement | null)

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    [stepFieldSelectorMap]
  )

  const saveDraftLabel = useMemo(() => {
    if (!isEditMode) return 'Save as Draft'
    if (originalStatus === 'published') return 'Save Changes as Draft'
    if (originalStatus === 'archived') return 'Save Changes as Draft'
    return 'Save Changes (Keep Draft)'
  }, [isEditMode, originalStatus])

  const publishLabel = useMemo(() => {
    if (!isEditMode) return 'Publish Service Package'
    return originalStatus === 'published'
      ? 'Save Changes & Publish'
      : 'Save Changes & Publish Package'
  }, [isEditMode, originalStatus])

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }))
  }, [])

  const updateFormData = useCallback(
    (updates: Partial<ServicePackageFormData>) => {
      setFormData(prev => ({
        ...prev,
        ...updates,
        updatedAt: new Date().toISOString(),
      }))

      // Clear validation errors for the current step
      setValidationErrors((prev: ValidationErrors) => {
        const newErrors = { ...prev }
        Object.keys(updates).forEach(key => {
          delete newErrors[key]
        })
        return newErrors
      })
    },
    []
  )

  const validateCurrentStep = useCallback((): boolean => {
    let validation: any = { isValid: true, errors: {} }

    switch (activeStep) {
      case 1:
        validation = validateStep1Basics(formData)
        break
      case 2:
        validation = validateStep2ClinicalCoding(formData)
        break
      case 3:
        validation = validateStep3PricingBilling(formData)
        break
      case 4:
        validation = validateStep4SchedulingRules(formData)
        break
      case 5:
        validation = validateStep5Documentation(formData)
        break
      case 6:
        validation = validateStep6VisibilityMatching(formData)
        break
      default:
        return true
    }

    if (!validation.isValid) {
      const sectionKey = STEP_SECTION_MAP[activeStep]
      setValidationErrors((prev: ValidationErrors) => ({
        ...prev,
        [sectionKey]: validation.errors,
      }))
      scrollToFirstErrorForStep(activeStep, validation.errors)
      return false
    }

    const sectionKey = STEP_SECTION_MAP[activeStep]
    setValidationErrors((prev: ValidationErrors) => {
      const next = { ...prev }
      delete next[sectionKey]
      return next
    })
    return true
  }, [activeStep, formData, scrollToFirstErrorForStep])

  const handleNext = useCallback(() => {
    if (validateCurrentStep() && activeStep < 6) {
      setActiveStep(prev => (prev + 1) as FormStep)
    }
  }, [activeStep, validateCurrentStep])

  const handleBack = useCallback(() => {
    if (activeStep > 1) {
      setActiveStep(prev => (prev - 1) as FormStep)
      setValidationErrors({})
    }
  }, [activeStep])

  const handleStepClick = useCallback(
    (step: FormStep) => {
      // In edit mode, allow jumping to any step
      // In create mode, allow jumping to any step (user can navigate freely)
      if (step !== activeStep) {
        setActiveStep(step)
        // Clear validation errors when jumping to a different step
        setValidationErrors({})
      }
    },
    [activeStep]
  )

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [activeStep])

  const handleSaveAsDraft = useCallback(async () => {
    setIsSubmitting(true)
    try {
      const draftData = { ...formData, status: 'draft' as const }

      if (onSave) {
        await onSave(draftData)
        showSnackbar(
          isEditMode
            ? 'Service package changes saved successfully! Redirecting...'
            : 'Service package saved as draft successfully! Redirecting...',
          'success'
        )
      } else {
        // Mock save - in real app, this would call an API
        await new Promise(resolve => setTimeout(resolve, 1000))
        showSnackbar(
          isEditMode
            ? 'Service package changes saved successfully! Redirecting...'
            : 'Service package saved as draft successfully! Redirecting...',
          'success'
        )
      }
      scheduleRedirectToList()
    } catch (error) {
      console.error('Error saving service package:', error)
      const message = getErrorMessage(
        error,
        'Failed to save service package. Please try again.'
      )
      showSnackbar(message, 'error')
    } finally {
      setIsSubmitting(false)
    }
  }, [formData, getErrorMessage, onSave, scheduleRedirectToList, showSnackbar])

  const handlePublish = useCallback(async () => {
    setIsSubmitting(true)
    try {
      // Validate all steps before publishing
      const fullValidation = validateAllSteps(formData)
      if (!fullValidation.isValid) {
        setValidationErrors(fullValidation.errors)
        showSnackbar(
          'Please fix all validation errors before publishing.',
          'error'
        )
        const stepOrder: FormStep[] = [1, 2, 3, 4, 5, 6]
        const firstInvalidStep = stepOrder.find(step => {
          const sectionKey = STEP_SECTION_MAP[step]
          const stepErrors = fullValidation.errors[sectionKey]
          return (
            stepErrors &&
            typeof stepErrors === 'object' &&
            Object.keys(stepErrors as ValidationErrors).length > 0
          )
        })

        if (firstInvalidStep) {
          setActiveStep(firstInvalidStep)
          const sectionKey = STEP_SECTION_MAP[firstInvalidStep]
          const stepErrors = fullValidation.errors[
            sectionKey
          ] as ValidationErrors

          window.setTimeout(() => {
            scrollToFirstErrorForStep(firstInvalidStep, stepErrors)
          }, 100)
        }

        setIsSubmitting(false)
        return
      }

      const publishedData = { ...formData, status: 'published' as const }

      if (onPublish) {
        await onPublish(publishedData)
        showSnackbar(
          isEditMode
            ? 'Service package updated and published successfully! Redirecting...'
            : 'Service package published successfully! Redirecting...',
          'success'
        )
      } else {
        // Mock publish - in real app, this would call an API
        await new Promise(resolve => setTimeout(resolve, 1500))
        showSnackbar(
          isEditMode
            ? 'Service package updated and published successfully! Redirecting...'
            : 'Service package published successfully! Redirecting...',
          'success'
        )
      }
      scheduleRedirectToList()
    } catch (error) {
      console.error('Error publishing service package:', error)
      const message = getErrorMessage(
        error,
        'Failed to publish service package. Please try again.'
      )
      showSnackbar(message, 'error')
    } finally {
      setIsSubmitting(false)
    }
  }, [
    formData,
    getErrorMessage,
    onPublish,
    scheduleRedirectToList,
    scrollToFirstErrorForStep,
    showSnackbar,
  ])

  const renderStepContent = () => {
    const sectionKey = STEP_SECTION_MAP[activeStep]
    const stepErrors =
      (validationErrors &&
        typeof validationErrors === 'object' &&
        validationErrors[sectionKey]) ||
      {}

    const commonProps = {
      formData,
      onUpdate: updateFormData,
      errors: stepErrors,
    }

    switch (activeStep) {
      case 1:
        return <Section1Basics {...commonProps} providerId={providerId} />
      case 2:
        return <Section2ClinicalCoding {...commonProps} />
      case 3:
        return (
          <Section3PricingBilling
            {...commonProps}
            initialInsurances={formData._initialInsurances}
          />
        )
      case 4:
        return <Section4SchedulingRules {...commonProps} />
      case 5:
        return <Section5Documentation {...commonProps} />
      case 6:
        return (
          <Section6VisibilityMatching
            {...commonProps}
            onSaveAsDraft={handleSaveAsDraft}
            onPublish={handlePublish}
            isSubmitting={isSubmitting}
            mode={mode}
            saveLabel={saveDraftLabel}
            publishLabel={publishLabel}
            originalStatus={originalStatus}
          />
        )
      default:
        return null
    }
  }

  const isStepValid = (step: FormStep): boolean => {
    switch (step) {
      case 1:
        return validateStep1Basics(formData).isValid
      case 2:
        return validateStep2ClinicalCoding(formData).isValid
      case 3:
        return validateStep3PricingBilling(formData).isValid
      case 4:
        return validateStep4SchedulingRules(formData).isValid
      case 5:
        return validateStep5Documentation(formData).isValid
      case 6:
        return validateStep6VisibilityMatching(formData).isValid
      default:
        return true
    }
  }

  return (
    <Container id="service-package-stepper-root" maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          px: { xs: 3, md: 4 },
          py: { xs: 3, md: 4 },
          borderRadius: 3,
          background:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.primary.main, 0.12)
              : `linear-gradient(135deg, ${alpha(
                theme.palette.primary.light,
                0.18
              )} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 3 }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.primary.light
                    : theme.palette.primary.dark,
                mb: 1,
              }}
            >
              {isEditMode ? 'Edit Service Package' : 'Create Service Package'}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.common.white, 0.75)
                    : alpha(theme.palette.text.primary, 0.7),
                maxWidth: 600,
              }}
            >
              {isEditMode
                ? 'Update your service package details. Adjust clinical coding, pricing, scheduling, and visibility settings as needed.'
                : 'Build a comprehensive service package for your therapy practice. Configure clinical coding, pricing, scheduling, and visibility settings.'}
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<BackIcon />}
            onClick={() => navigate(`/agency-provider-service-packages/${selectedProviderId}`)}
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.25,
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 'none',
              alignSelf: { xs: 'stretch', md: 'center' },
            }}
          >
            Back to Service Packages
          </Button>
        </Stack>
      </Paper>

      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Stepper
          activeStep={activeStep - 1}
          alternativeLabel={!isMobile}
          orientation={isMobile ? 'vertical' : 'horizontal'}
        >
          {steps.map((label, index) => {
            const stepNumber = (index + 1) as FormStep
            const isCompleted = stepNumber < activeStep
            const hasError = stepNumber < activeStep && !isStepValid(stepNumber)
            // In edit mode, allow clicking all steps. In create mode, also allow free navigation
            const isClickable = true

            return (
              <Step key={label} completed={isCompleted}>
                <StepLabel
                  error={hasError}
                  onClick={() => handleStepClick(stepNumber)}
                  sx={{
                    cursor: 'pointer',
                    '& .MuiStepLabel-label': {
                      fontSize: isMobile ? '0.875rem' : '1rem',
                      cursor: 'pointer',
                      transition: 'color 0.2s ease',
                      '&:hover': {
                        color: theme.palette.primary.main,
                        textDecoration: 'underline',
                      },
                    },
                    '& .MuiStepLabel-iconContainer': {
                      cursor: 'pointer',
                      transition: 'color 0.2s ease',
                      '&:hover': {
                        '& .MuiSvgIcon-root': {
                          color: theme.palette.primary.main,
                        },
                      },
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            )
          })}
        </Stepper>
      </Paper>

      {/* Step Content */}
      <Paper sx={{ p: 4, mb: 4 }}>{renderStepContent()}</Paper>

      {/* Navigation Buttons (hidden for step 6 as it has its own buttons) */}
      {activeStep < 6 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={activeStep <= 1 || isSubmitting}
            startIcon={<BackIcon />}
          >
            Back
          </Button>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleSaveAsDraft}
              disabled={isSubmitting}
            >
              {saveDraftLabel}
            </Button>

            <Button
              variant="contained"
              onClick={handleNext}
              disabled={isSubmitting}
              endIcon={<ForwardIcon />}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
